if (!customElements.get("standard-loop-buy-box")) {
  customElements.define(
    "standard-loop-buy-box",
    class StandardLoopBuyBox extends HTMLElement {
      connectedCallback() {
        this.product = JSON.parse(
          this.querySelector("[data-standard-product-json]").textContent,
        );
        this.currency = window.Shopify?.currency?.active || "AUD";
        this.oneTimeOnly = this.dataset.oneTimeOnly === "true";
        this.subscribe = !this.oneTimeOnly && this.dataset.defaultPurchase === "subscription";
        this.quantity = 1;
        this.people = 1;
        this.jarsPerMonth = Number(this.dataset.jarsPerMonth) || 1;

        const initialVariant =
          this.product.variants.find(
            (variant) =>
              String(variant.id) === this.dataset.initialVariant &&
              variant.available &&
              !this.isTwoPeopleVariant(variant),
          ) || this.product.variants.find(
            (variant) => variant.available && !this.isTwoPeopleVariant(variant),
          );
        this.duration = this.variantDuration(initialVariant);

        this.variantInput = this.querySelector('[name="id"]');
        this.planInput = this.querySelector('[name="selling_plan"]');
        this.quantityInput = this.querySelector('[name="quantity"]');
        this.submitButton = this.querySelector("[data-standard-submit]");

        this.querySelectorAll("[data-standard-duration]").forEach((button) => {
          button.addEventListener("click", () => {
            const duration = Number(button.dataset.standardDuration);
            const variant = this.variantFor(
              duration,
              this.subscribe ? this.people : 1,
            );
            if (!variant?.available) return;
            this.duration = duration;
            if (this.subscribe && !this.allocationFor(variant)) {
              this.subscribe = false;
            }
            this.updateUrl();
            this.render();
          });
        });

        this.querySelector("[data-standard-subscription]").addEventListener(
          "click",
          () => {
            if (!this.allocationFor(this.currentVariant())) return;
            this.subscribe = true;
            this.quantity = 1;
            this.render();
          },
        );
        this.querySelector("[data-standard-one-time]").addEventListener(
          "click",
          () => {
            this.people = 1;
            this.subscribe = false;
            this.updateUrl();
            this.render();
          },
        );
        this.querySelectorAll("[data-standard-people]").forEach((button) => {
          button.addEventListener("click", () => {
            const people = Number(button.dataset.standardPeople);
            if (!this.availableCombination(this.duration, people)) return;
            this.people = people;
            this.subscribe = true;
            this.quantity = 1;
            this.updateUrl();
            this.render();
          });
        });
        this.querySelector("[data-standard-quantity-minus]").addEventListener(
          "click",
          () => {
            this.quantity = Math.max(1, this.quantity - 1);
            this.render();
          },
        );
        this.querySelector("[data-standard-quantity-plus]").addEventListener(
          "click",
          () => {
            this.quantity += 1;
            this.render();
          },
        );

        if (!this.currentVariant()) {
          const fallback = this.product.variants.find(
            (variant) => variant.available && !this.isTwoPeopleVariant(variant),
          );
          this.duration = this.variantDuration(fallback);
        }
        if (this.subscribe && !this.allocationFor(this.currentVariant())) {
          this.subscribe = false;
        }
        this.render();
      }

      money(cents) {
        return new Intl.NumberFormat("en-AU", {
          style: "currency",
          currency: this.currency,
          minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
          maximumFractionDigits: 2,
        }).format(cents / 100);
      }

      isTwoPeopleVariant(variant) {
        return /2\s*(bottle|pack|people)|6\s*(bottle|pack)/i.test(
          variant?.title || "",
        );
      }

      variantDuration(variant) {
        return /3\s*month|90\s*day|3\s*bottle/i.test(variant?.title || "")
          ? 3
          : 1;
      }

      variantFor(duration, people = this.people) {
        return this.product.variants.find(
          (variant) =>
            this.isTwoPeopleVariant(variant) === (people === 2) &&
            this.variantDuration(variant) === duration,
        );
      }

      currentVariant() {
        return this.variantFor(this.duration);
      }

      availableCombination(duration, people) {
        const variant = this.variantFor(duration, people);
        return Boolean(variant?.available && this.allocationFor(variant));
      }

      planName(planId) {
        for (const group of this.product.selling_plan_groups || []) {
          const plan = group.selling_plans.find(
            (candidate) => String(candidate.id) === String(planId),
          );
          if (plan) return plan.name;
        }
        return "";
      }

      allocationFor(variant) {
        if (!variant) return null;
        const duration = this.variantDuration(variant);
        return (
          (variant.selling_plan_allocations || []).find((allocation) => {
            const name = this.planName(allocation.selling_plan_id);
            if (/2\s*month/i.test(name)) return false;
            return duration === 3
              ? /3\s*month|90\s*day/i.test(name)
              : /every\s*month|30\s*day|1\s*month/i.test(name);
          }) || null
        );
      }

      displayPrice(variant, subscribe = this.subscribe) {
        const allocation = this.allocationFor(variant);
        if (subscribe && allocation) {
          return {
            price: allocation.price,
            compare:
              variant.compare_at_price > allocation.price
                ? variant.compare_at_price
                : variant.price,
          };
        }
        return {
          price: variant.price,
          compare:
            variant.compare_at_price > variant.price
              ? variant.compare_at_price
              : null,
        };
      }

      savingsPercent(price, compare) {
        if (!compare || compare <= price) return 0;
        return Math.round(((compare - price) * 100) / compare);
      }

      planDiscount(variant, allocation) {
        if (!allocation) return 0;
        const compare =
          variant.compare_at_price > allocation.price
            ? variant.compare_at_price
            : variant.price;
        if (compare <= allocation.price) return 0;
        return Math.round(
          ((compare - allocation.price) * 100) / compare,
        );
      }

      updateUrl() {
        const variant = this.currentVariant();
        if (!variant) return;
        const url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState({}, "", url);
      }

      renderDurations() {
        this.querySelectorAll("[data-standard-duration]").forEach((button) => {
          const duration = Number(button.dataset.standardDuration);
          const people = this.subscribe ? this.people : 1;
          const variant = this.variantFor(duration, people);
          const allocation = this.allocationFor(variant);
          const canSubscribe = Boolean(allocation);
          const display = variant
            ? this.displayPrice(variant, this.subscribe && canSubscribe)
            : null;
          const compare = display?.compare;
          const discount = display
            ? this.savingsPercent(display.price, compare)
            : 0;

          button.disabled =
            !variant?.available || (this.subscribe && !canSubscribe);
          button.setAttribute(
            "aria-pressed",
            String(duration === this.duration),
          );
          button.querySelector("[data-standard-duration-price]").textContent =
            display ? this.money(display.price) : "Unavailable";
          const jarCount = duration * people * this.jarsPerMonth;
          button.querySelector("[data-standard-duration-label]").textContent =
            `${duration} Month${duration === 1 ? "" : "s"} (${jarCount} Jar${jarCount === 1 ? "" : "s"})`;
          button.querySelector("[data-standard-duration-unit]").textContent =
            display
              ? `${this.money(Math.round(display.price / jarCount))} per jar`
              : "";
          const badge = button.querySelector("[data-standard-duration-badge]");
          badge.textContent = discount ? `Save ${discount}%` : "";
          badge.hidden = !discount;
        });
      }

      renderPurchaseOptions(variant, allocation) {
        const subscriptionDisplay = this.displayPrice(variant, true);
        const oneTimeVariant = this.variantFor(this.duration, 1);
        const oneTimeDisplay = this.displayPrice(oneTimeVariant, false);
        const subscriptionDiscount = this.planDiscount(variant, allocation);
        const subscriptionTrigger = this.querySelector(
          "[data-standard-subscription]",
        );
        const subscriptionCard = this.querySelector(
          "[data-standard-subscription-card]",
        );
        const subscriptionBestValue = this.querySelector(
          "[data-standard-subscription-best-value]",
        );
        const oneTimeCard = this.querySelector("[data-standard-one-time]");

        subscriptionTrigger.disabled = !allocation;
        subscriptionBestValue.hidden = this.duration !== 3;
        subscriptionTrigger.setAttribute(
          "aria-pressed",
          String(this.subscribe),
        );
        subscriptionCard.setAttribute(
          "data-selected",
          String(this.subscribe),
        );
        oneTimeCard.setAttribute("aria-pressed", String(!this.subscribe));
        oneTimeCard.setAttribute("data-selected", String(!this.subscribe));

        this.querySelector("[data-standard-subscription-label]").textContent =
          `${this.dataset.subscribeLabel}${subscriptionDiscount ? ` ${subscriptionDiscount}%` : ""}`;
        this.querySelector("[data-standard-subscription-price]").textContent =
          allocation ? this.money(subscriptionDisplay.price) : "Unavailable";
        const subscriptionCompare = this.querySelector(
          "[data-standard-subscription-compare]",
        );
        subscriptionCompare.textContent = subscriptionDisplay.compare
          ? this.money(subscriptionDisplay.compare)
          : "";
        subscriptionCompare.hidden = !subscriptionDisplay.compare;

        this.querySelector("[data-standard-one-time-price]").textContent =
          this.money(oneTimeDisplay.price);
        const oneTimeCompare = this.querySelector(
          "[data-standard-one-time-compare]",
        );
        oneTimeCompare.textContent = oneTimeDisplay.compare
          ? this.money(oneTimeDisplay.compare)
          : "";
        oneTimeCompare.hidden = !oneTimeDisplay.compare;

        this.querySelector("[data-standard-subscription-details]").hidden =
          !this.subscribe;
        this.querySelector("[data-standard-delivery]").textContent =
          this.duration === 3 ? "3 months" : "month";
        const giftBenefit = this.querySelector("[data-standard-gift-benefit]");
        if (giftBenefit) {
          giftBenefit.textContent =
            this.duration === 3
              ? this.dataset.longGiftsBenefit
              : this.dataset.monthlyGiftsBenefit;
        }
        this.querySelectorAll("[data-standard-gifts]").forEach((gifts) => {
          gifts.hidden =
            !this.subscribe ||
            Number(gifts.dataset.standardGifts) !== this.duration;
        });
      }

      renderPeople() {
        const group = this.querySelector("[data-standard-people-group]");
        if (group) group.hidden = !this.subscribe;
        this.querySelectorAll("[data-standard-people]").forEach((button) => {
          const people = Number(button.dataset.standardPeople);
          button.disabled = !this.availableCombination(this.duration, people);
          button.setAttribute("aria-pressed", String(people === this.people));
        });
      }

      render() {
        const variant = this.currentVariant();
        if (!variant) return;
        const allocation = this.allocationFor(variant);
        if (this.oneTimeOnly) this.subscribe = false;
        if (this.subscribe && !allocation) this.subscribe = false;
        if (this.subscribe) this.quantity = 1;
        const selectedDisplay = this.displayPrice(variant);

        this.variantInput.value = variant.id;
        this.quantityInput.value = this.quantity;
        this.planInput.disabled = !this.subscribe || !allocation;
        this.planInput.value =
          this.subscribe && allocation ? allocation.selling_plan_id : "";

        this.renderDurations();
        this.renderPurchaseOptions(variant, allocation);
        this.renderPeople();
        this.querySelector("[data-standard-quantity-value]").textContent =
          this.quantity;
        this.querySelector("[data-standard-quantity]").hidden = this.subscribe;

        const shipping = this.querySelector("[data-standard-shipping]");
        if (shipping) {
          shipping.hidden =
            !this.subscribe && shipping.dataset.subscriptionOnly === "true";
        }

        this.querySelector("[data-standard-submit-price]").textContent =
          this.money(selectedDisplay.price * this.quantity);
        this.submitButton.disabled = !variant.available;
        this.querySelector("[data-standard-submit-label]").textContent =
          variant.available ? this.dataset.addLabel : this.dataset.soldOutLabel;
      }
    },
  );
}
